import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import BlockIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import LockResetIcon from '@mui/icons-material/LockResetOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { createUser, listUsers, resetUserPassword, setUserActive, updateUser } from '../../api/users';
import { listRoles } from '../../api/roles';
import { listBranches } from '../../api/branches';
import { UserListItem } from '../../types/user';
import { CreateUserFormValues, UpdateUserFormValues } from '../../validation/user';
import { TableToolbar } from '../../components/table/TableToolbar';
import { PageHeader } from '../../components/PageHeader';
import { useAuth } from '../../context/authContext';
import { UserFormDialog } from './UserFormDialog';
import { ResetUserPasswordDialog } from './ResetUserPasswordDialog';

export const UsersListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, hasPermission } = useAuth();
  const canResetOthers = hasPermission('users', 'reset_password');

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserListItem | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [resetPasswordTarget, setResetPasswordTarget] = useState<UserListItem | null>(null);

  // "+ New" menu bug fix (QuickCreateButton navigates here with
  // {quickCreate: true}) — this never auto-opened the New User dialog
  // before. Depends on location.state (not `[]`) so it also fires when
  // the user is already on /users and clicks "+ New -> New User" again —
  // see LeadsListPage.tsx's identical fix for the full explanation.
  useEffect(() => {
    if ((location.state as { quickCreate?: boolean } | null)?.quickCreate) {
      setEditingUser(null);
      setFormError(null);
      setDialogOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const queryClient = useQueryClient();

  const usersQuery = useQuery({
    queryKey: ['users', paginationModel.page, paginationModel.pageSize, search],
    queryFn: () =>
      listUsers({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: search || undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: listRoles });
  const branchesQuery = useQuery({ queryKey: ['branches'], queryFn: () => listBranches() });

  const invalidateUsers = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const createMutation = useMutation({
    mutationFn: (values: CreateUserFormValues) =>
      createUser({
        ...values,
        phone: values.phone || undefined,
        branchId: values.branchId || undefined,
      }),
    onSuccess: () => {
      invalidateUsers();
      setDialogOpen(false);
      setFormError(null);
    },
    onError: (err) => setFormError(extractErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: number; values: UpdateUserFormValues }) =>
      updateUser(id, { ...values, phone: values.phone || null, branchId: values.branchId ?? null }),
    onSuccess: () => {
      invalidateUsers();
      setDialogOpen(false);
      setFormError(null);
    },
    onError: (err) => setFormError(extractErrorMessage(err)),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) => setUserActive(id, isActive),
    onSuccess: invalidateUsers,
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Parameters<typeof resetUserPassword>[1] }) =>
      resetUserPassword(id, payload),
    onSuccess: invalidateUsers,
  });

  const columns: GridColDef<UserListItem>[] = useMemo(
    () => [
      { field: 'employeeCode', headerName: 'Code', width: 110 },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        valueGetter: (_value, row) => `${row.firstName} ${row.lastName}`,
      },
      { field: 'email', headerName: 'Email', flex: 1 },
      {
        field: 'role',
        headerName: 'Role',
        width: 160,
        valueGetter: (_value, row) => row.role.name,
      },
      {
        field: 'branch',
        headerName: 'Branch',
        width: 150,
        valueGetter: (_value, row) => row.branch?.name ?? '—',
      },
      {
        field: 'isActive',
        headerName: 'Status',
        width: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? 'Active' : 'Inactive'}
            color={params.value ? 'success' : 'default'}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 160,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const isOwnRow = params.row.id === currentUser?.id;
          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditingUser(params.row);
                    setFormError(null);
                    setDialogOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {isOwnRow && (
                <Tooltip title="Change password">
                  <IconButton size="small" onClick={() => navigate('/change-password')}>
                    <LockResetIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              )}
              {!isOwnRow && canResetOthers && (
                <Tooltip title="Reset password">
                  <IconButton size="small" onClick={() => setResetPasswordTarget(params.row)}>
                    <LockResetIcon fontSize="small" color="warning" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={params.row.isActive ? 'Deactivate' : 'Activate'}>
                <IconButton
                  size="small"
                  onClick={() =>
                    toggleActiveMutation.mutate({ id: params.row.id, isActive: !params.row.isActive })
                  }
                >
                  {params.row.isActive ? (
                    <BlockIcon fontSize="small" />
                  ) : (
                    <CheckCircleIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [toggleActiveMutation, currentUser?.id, canResetOthers, navigate],
  );

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={GroupOutlinedIcon}
        color="#2F6FED"
        title="Users"
        subtitle="Manage staff accounts, roles and branch access."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingUser(null);
              setFormError(null);
              setDialogOpen(true);
            }}
          >
            New user
          </Button>
        }
      />

      <TextField
        placeholder="Search by name, email or employee code…"
        size="small"
        sx={{ mb: 2, width: 360 }}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={usersQuery.data?.data ?? []}
          columns={columns}
          rowCount={usersQuery.data?.meta.total ?? 0}
          loading={usersQuery.isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          slots={{ toolbar: TableToolbar }}
          disableRowSelectionOnClick
        />
      </Box>

      <UserFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editingUser={editingUser}
        roles={rolesQuery.data ?? []}
        branches={branchesQuery.data ?? []}
        errorMessage={formError}
        onCreate={async (values) => {
          await createMutation.mutateAsync(values);
        }}
        onUpdate={async (id, values) => {
          await updateMutation.mutateAsync({ id, values });
        }}
      />

      <ResetUserPasswordDialog
        open={!!resetPasswordTarget}
        userName={resetPasswordTarget ? `${resetPasswordTarget.firstName} ${resetPasswordTarget.lastName}` : ''}
        onClose={() => setResetPasswordTarget(null)}
        onSubmit={(payload) => resetPasswordMutation.mutateAsync({ id: resetPasswordTarget!.id, payload })}
      />
    </Box>
  );
};

const extractErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    return err.response?.data?.message ?? 'Something went wrong.';
  }
  return 'Something went wrong.';
};
